import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch } from '../../../store/index.ts'
import { setAuthenticatedPlanner, setGuests, setSeatingPlan, saveSession, loadAuthFromStorage } from '../../../store/seatingSlice.ts'
import { loginWithPlannerPin, fetchPlannerGuests, fetchSeatingPlan } from '../../../endpoint/index.ts'
import Navbar from '../../../components/organism/navbar/index.tsx'
import Footer from '../../../components/organism/footer/index.tsx'
import PlanerBtn from '../../../components/atom/planer-btn/index.tsx'
import './index.css'

const WeddingPlanerLogin: React.FC = () => {
	const { t } = useTranslation('planner')
	const navigate = useNavigate()
	const dispatch = useAppDispatch()
	const [pin, setPin] = useState<string>('')
	const [loading, setLoading] = useState<boolean>(false)
	const [error, setError] = useState<string | null>(null)

	// Check if already authenticated
	useEffect(() => {
		const stored = loadAuthFromStorage()
		if (stored?.authenticatedPlanner) {
			navigate(`/weddingPlanner/${stored.authenticatedPlanner.id}`)
		}
	}, [navigate])

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault()
		if (!pin.trim()) return

		setLoading(true)
		setError(null)

		try {
			// No signInAnonymously here any more. An anonymous session was
			// enough back when the rules asked only "is anyone signed in", but
			// `planners` now wants a plannerFor claim, and only the server can
			// issue one — loginWithPlannerPin gets it from plannerPinLogin and
			// signs in with it.
			const planner = await loginWithPlannerPin(pin.trim())

			if (planner) {
				dispatch(setAuthenticatedPlanner(planner))

				// Fetch guests and seating plan
				const [guestsData, seatingPlanData] = await Promise.all([
					fetchPlannerGuests(planner.id),
					fetchSeatingPlan(planner.id)
				])

				dispatch(setGuests(guestsData))
				dispatch(setSeatingPlan(seatingPlanData))
				dispatch(saveSession())

				// Navigate to the planner app
				navigate(`/weddingPlanner/${planner.id}`)
			} else {
				setError(t('login.errors.invalidPin'))
			}
		} catch (err) {
			setError(t('login.errors.generic'))
			console.error('Login error:', err)
		} finally {
			setLoading(false)
		}
	}

	return (
		<div className='wedding-planer-login-page'>
			<Navbar scrollToContact={() => {}} />

			<div className='login-page-content'>
				<div className='login-page-card'>
					<img
						src={require('../../../assets/weddingCamera/logo-with-text.png')}
						alt='Wedding Camera Logo'
						className='login-page-logo'
					/>
					<h1>{t('login.title')}</h1>
					<p className='login-subtitle'>{t('login.subtitle')}</p>

					<form className='login-form' onSubmit={handleSubmit}>
						<input
							type='text'
							value={pin}
							onChange={(e) => setPin(e.target.value)}
							placeholder={t('login.pinPlaceholder')}
							className='login-pin-input'
							disabled={loading}
							autoFocus
						/>

						{error && <p className='login-error'>{error}</p>}

						<PlanerBtn
							title={loading ? t('login.loggingIn') : t('login.loginButton')}
							disabled={loading || !pin.trim()}
							type='submit'
							fullWidth
						/>
					</form>

					{/* The planner is free now, so registration is the main way in -
					    not a footnote under the PIN field. The demo box that used to sit
					    here published a working PIN to anyone who opened the page. */}
					<div className='login-register-box'>
						<h3>{t('login.noPin')}</h3>
						<PlanerBtn
							title={t('login.registerCta')}
							onClick={() => navigate('/weddingPlanner/register')}
							fullWidth
						/>
					</div>
				</div>
			</div>

			<Footer />
		</div>
	)
}

export default WeddingPlanerLogin
